import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ThresholdSliders, type ThresholdValue } from "@/monitors/components/ThresholdSliders";
import { useCommitMonitor } from "@/monitors/api";
import type { MonitorIR } from "@/monitors/schemas";

interface Props {
  draftId: string;
  ir: MonitorIR;
  irOverrides: Partial<MonitorIR> | null;
  suggestedThreshold: number;
  onSaved: (monitorId: string) => void;
}

export function Step3Commit({ draftId, ir, irOverrides, suggestedThreshold, onSaved }: Props) {
  const [thresholds, setThresholds] = useState<ThresholdValue>({
    candidate_threshold: suggestedThreshold,
    verify_threshold: 0.62,
    confirm_threshold: 0.70,
    cooldown_seconds: 0,
    max_matches_per_day: null,
  });
  const commit = useCommitMonitor();

  const save = async () => {
    const res = await commit.mutateAsync({
      draft_id: draftId,
      ...thresholds,
      ir_overrides: irOverrides ?? undefined,
    });
    onSaved(res.id);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4 space-y-2">
          <h3 className="font-semibold">Review and save</h3>
          <p className="text-sm text-muted-foreground">
            Required entities: {ir.required_entities.join(", ") || "(none)"}
          </p>
          <p className="text-sm text-muted-foreground">
            Polarity: <strong>{ir.polarity}</strong>
            {ir.action_class && (
              <> · Action: <strong>{ir.action_class}</strong></>
            )}
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <h3 className="font-semibold mb-3">Thresholds</h3>
          <ThresholdSliders
            value={thresholds}
            onChange={(patch) => setThresholds((p) => ({ ...p, ...patch }))}
          />
        </CardContent>
      </Card>

      {commit.error && (
        <p className="text-sm text-red-600">
          {commit.error.message ?? "Couldn't save the monitor."}
        </p>
      )}

      <div className="flex justify-end">
        <Button onClick={save} disabled={commit.isPending}>
          {commit.isPending ? "Saving…" : "Save monitor"}
        </Button>
      </div>
    </div>
  );
}
